"use client";

import { OfferInvestmentMethod } from "@/types";
import useFeatureFlag from "./featureFlag";
import { useInvestmentAmountFlags } from "./useInvestmentAmountFlags";

/**
 * Single source of truth for how a capital raise is presented on the application form.
 *
 * `isUnitBased` is the *denomination* of the raise: it decides that the minimum reads in units
 * and that the application is submitted as a unit count. `isDualEntry` is only about the
 * fields on screen. They are deliberately separate — a UNITS raise is unit-denominated *and*
 * shows a dollar field, because that field is an input aid that converts.
 *
 * Submitting dollars against a UNITS raise is rejected by offer-service
 * (`allocation_request.go`: "capital raise does not accept amount-based allocations"), so
 * `isUnitBased` must keep driving the submitted denomination even under dual entry.
 */
export function useUnitBasedOffer(
	investmentMethod?: OfferInvestmentMethod | null,
) {
	const isMultiBatchAllocationsEnabled = useFeatureFlag(
		"MULTI_BATCH_ALLOCATIONS",
	);
	const { isInvestmentAmountOnlyUI } = useInvestmentAmountFlags();

	return {
		isUnitBased:
			isMultiBatchAllocationsEnabled &&
			investmentMethod === OfferInvestmentMethod.Units,
		isMultiBatchAllocationsEnabled,
		isDualEntry: resolveIsDualEntry({
			investmentMethod,
			isMultiBatchAllocationsEnabled,
			isInvestmentAmountOnlyUI,
		}),
	};
}

function resolveIsDualEntry({
	investmentMethod,
	isMultiBatchAllocationsEnabled,
	isInvestmentAmountOnlyUI,
}: {
	investmentMethod?: OfferInvestmentMethod | null;
	isMultiBatchAllocationsEnabled: boolean;
	isInvestmentAmountOnlyUI: boolean;
}): boolean {
	if (isMultiBatchAllocationsEnabled) {
		switch (investmentMethod) {
			case OfferInvestmentMethod.Dollars:
				return false;
			case OfferInvestmentMethod.Units:
			case OfferInvestmentMethod.Both:
				return true;
			default:
				break;
		}
	}

	return !isInvestmentAmountOnlyUI;
}
